/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { DateTime, Interval } from 'luxon';
import { memo, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Alert, Button, Paper, Popover, Typography } from '@mui/material';

import { HUModal2, HUModal2Props } from '../../../../components/HUModal2/HUModal2';
import { parseMaintenanceWindow } from '../../../../containers/CreateClusterOverlay/utils';
import { EntityId } from '../../../../types/api/EntityId';
import { getDateFormatByLocale } from '../../../../utils/getDateFormatByLocale';
import { useUpgradeClusterDialogStyles } from './UpgradeClusterDialog.styles';

type UpgradeClusterDialogProps = Pick<HUModal2Props, 'open'> &
  Required<Pick<HUModal2Props, 'onCancel'>> & {
    onUpgrade: (schedule?: boolean) => Promise<void>;
    currentVersion: number;
    latestBackupTimestamp: string | null;
    maintenanceWindows: string[] | null;
  };

type UpgradableClusterType = {
  id: EntityId | null;
  anchor: HTMLButtonElement | null;
  isOpen: boolean;
};

export const UpgradeClusterDialog = memo<UpgradeClusterDialogProps>(props => {
  const { open, onCancel, onUpgrade, currentVersion, latestBackupTimestamp, maintenanceWindows } = props;

  const { t, i18n } = useTranslation();
  const classes = useUpgradeClusterDialogStyles();

  const wantToUpgradeInit: UpgradableClusterType = {
    id: null,
    anchor: null,
    isOpen: false,
  };

  const [wantToUpgrade, setWantToUpgrade] = useState<UpgradableClusterType>(wantToUpgradeInit);

  const handlePopoverToggle = (args: {
    event: React.MouseEvent<HTMLButtonElement>;
    id: UpgradableClusterType['id'];
  }) => {
    const { event, id } = args;
    const isPopoverOpen = wantToUpgrade.isOpen;
    setWantToUpgrade({
      id: !isPopoverOpen ? id : null,
      anchor: !isPopoverOpen ? event.currentTarget : null,
      isOpen: !isPopoverOpen,
    });
  };

  const handlePopoverCancel = () => {
    setWantToUpgrade(wantToUpgradeInit);
  };

  const formattedTimestamp = useMemo(
    () =>
      latestBackupTimestamp
        ? DateTime.fromISO(latestBackupTimestamp).toFormat(getDateFormatByLocale(i18n.language))
        : '',
    [latestBackupTimestamp, i18n],
  );

  const warningi18nKey = useMemo(
    () =>
      latestBackupTimestamp
        ? 'cluster:details.header.upgrade_version_form.warning_latest_backup'
        : 'cluster:details.header.upgrade_version_form.warning_no_backup',
    [latestBackupTimestamp],
  );

  const isNowInMaintenanceWindows = useMemo(() => {
    if (!maintenanceWindows) {
      return true;
    }

    const now = DateTime.now().setZone('utc');

    return maintenanceWindows.some(window => {
      const { weekday, startTimeUtc, endTimeUtc } = parseMaintenanceWindow(window);

      if (weekday) {
        const currentWeekday = now.toFormat('ccc');
        if (currentWeekday !== weekday) return false;
      }

      const start = now.set({
        hour: startTimeUtc.hour,
        minute: startTimeUtc.minute,
        second: 0,
        millisecond: 0,
      });

      const end = now.set({
        hour: endTimeUtc.hour,
        minute: endTimeUtc.minute,
        second: 0,
        millisecond: 0,
      });

      return Interval.fromDateTimes(start, end).contains(now);
    });
  }, [maintenanceWindows]);

  const onOk = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isNowInMaintenanceWindows) {
      void onUpgrade();
    } else {
      event.stopPropagation();
      handlePopoverToggle({ event, id: 'upgrade-button' });
    }
  };

  const onConfirm = () => {
    handlePopoverCancel();
    setTimeout(() => onUpgrade(true), 400);
  };

  return (
    <>
      <HUModal2
        open={open}
        okButtonText={t('cluster:details.header.upgrade_version_form.submit')}
        cancelButtonText={t('cluster:details.header.name.cancel')}
        okButtonProps={{ id: 'upgrade-button' }}
        onCancel={onCancel}
        onOk={onOk}
        onClose={onCancel}
        size="big"
        disableRestoreFocus
        title={t('cluster:details.header.upgrade_version_form.title', { version: currentVersion + 1 })}
      >
        <Alert severity="warning" classes={{ icon: classes.icon }}>
          <Typography style={{ fontSize: '14px' }}>
            <Trans i18nKey={warningi18nKey} values={{ dateTime: formattedTimestamp }} components={{ b: <b /> }} />
          </Typography>
          <Typography style={{ fontSize: '14px' }}>
            {t('cluster:details.header.upgrade_version_form.warning_message')}
          </Typography>
        </Alert>
      </HUModal2>
      <Popover
        open={wantToUpgrade.isOpen}
        anchorEl={wantToUpgrade.anchor}
        onClose={handlePopoverCancel}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Paper style={{ maxWidth: '600px' }} classes={{ root: classes.popover }}>
          <Typography textAlign="center">
            <Trans
              i18nKey="cluster:details.header.upgrade_version_form.confirm_message"
              values={{ maintenanceWindows: maintenanceWindows?.toString().replace(/,/g, ', ') }}
              components={{ b: <b /> }}
            />
          </Typography>
          <Typography style={{ marginTop: '10px' }} textAlign="center">
            {t('cluster:details.header.upgrade_version_form.confirm_question')}
          </Typography>
          <div
            style={{ display: 'flex', justifyContent: 'center', gap: '40px', marginTop: '20px', alignItems: 'center' }}
          >
            <Button data-cy="confirm-upgrade-popover-button" onClick={onConfirm} variant="contained">
              {t('common:label.ok')}
            </Button>
            <Button onClick={handlePopoverCancel} variant="text">
              {t('common:label.cancel')}
            </Button>
          </div>
        </Paper>
      </Popover>
    </>
  );
});
